import {
  DiscoveryApi,
  FetchApi,
  OAuthApi,
} from '@backstage/frontend-plugin-api';
import {
  Configuration,
  CreateOverrideRequestStateEnum,
  AuraPortalApi,
  CreateEntitlementRequest,
  UpdateEntitlementRequest,
  GetResponse,
  Transition as APITransition,
  EntitlementResponse,
  ClaimEntitlementRequest,
  type V1AuraPortalAccountsGetRequest,
} from './billing-account-api-sdk';
import {
  Configuration as ProductCatalogueConfiguration,
  DefaultApi as ProductCatalogueApi,
} from './product-catalogue-api-sdk';
import {
  BillingAccount,
  BillingAccountApi,
  BillingAccountError,
  BillingAccountRecord,
  CatalogueVersion,
  CreateEntitlementParams,
  Entitlement,
  Marketplace,
  Override,
  OverrideState,
  PaymentProcessor,
  Transition,
} from './types.ts';

export class UbsAccountsApi implements BillingAccountApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly oktaAuthApi: OAuthApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
    oktaAuthApi: OAuthApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.oktaAuthApi = options.oktaAuthApi;
  }

  private async getAccountsClient(): Promise<AuraPortalApi> {
    const proxyBaseUrl = await this.discoveryApi.getBaseUrl('proxy');
    const oktaToken = await this.oktaAuthApi.getAccessToken();
    const config = new Configuration({
      basePath: `${proxyBaseUrl}/billing-account-api`,
      fetchApi: this.fetchApi.fetch,
      headers: {
        'Aura-Portal-Okta-Token': oktaToken,
      },
    });
    return new AuraPortalApi(config);
  }

  // product-catalogue-api validates no token of its own, so no Okta token is sent.
  private async getProductCatalogueClient(): Promise<ProductCatalogueApi> {
    const proxyBaseUrl = await this.discoveryApi.getBaseUrl('proxy');
    const config = new ProductCatalogueConfiguration({
      basePath: `${proxyBaseUrl}/product-catalogue-api`,
      fetchApi: this.fetchApi.fetch,
    });
    return new ProductCatalogueApi(config);
  }

  // Helper method to map GetResponse to BillingAccount
  private mapToBillingAccount(r: GetResponse): BillingAccount {
    return {
      billingID: r.billingID as string,
      organizationID: r.organizationID as string,
      organizationProfile: (r as any).organizationProfile ?? null,
      projectIDs: Array.isArray((r as any).projectIDs)
        ? ((r as any).projectIDs as string[])
        : [],
      billingModel: (r as any).billingModel ?? '',
      billingProcessor: (r as any).billingProcessor ?? '',
      paymentMethod: (r as any).paymentMethod ?? '',
      pricingPlan: (r as any).pricingPlan ?? '',
      status: (r as any).status ?? '',
      email: (r as any).email ?? '',
      name: (r as any).name ?? '',
      reason: (r as any).reason ?? '',
      isFunded: Boolean((r as any).isFunded),
      unfundedReason: (r as any).unfundedReason ?? '',
      productCatalogueVersion: (r as any).productCatalogueVersion ?? '',
    };
  }

  // Helper method to map EntitlementResponse to Entitlement
  private mapToEntitlement(r: EntitlementResponse): Entitlement {
    return {
      id: r.id,
      amount: r.amount,
      costBasis: r.costBasis,
      status: r.status,
      type: r.type,
      start: r.start.toISOString(), // Convert Date to RFC3339 string
      end: r.end.toISOString(), // Convert Date to RFC3339 string
      claimedAt: r.claimedAt?.toISOString() ?? undefined, // Convert Date to RFC3339 string
      orbCreditBlockID: r.orbCreditBlockID ?? undefined,
      salesforceSubID: r.salesforceSubID ?? undefined,
      rollOverCreditBlockID: r.rollOverCreditBlockID ?? undefined,
      rolloverPercentage: r.rolloverPercentage ?? undefined,
      provisioningID: r.provisioningID ?? undefined,
    };
  }

  // Helper method to fetch payment processor for an account
  private async fetchPaymentProcessor(
    client: AuraPortalApi,
    billingID: string,
  ): Promise<PaymentProcessor | BillingAccountError | undefined> {
    try {
      const response =
        await client.v1AuraPortalAccountsBillingIDPaymentProcessorGet({
          billingID,
        });
      const ppData = (response as any)?.paymentProcessor;
      if (ppData) {
        return {
          paymentProcessorID: ppData.paymentProcessorID ?? '',
          type: ppData.type ?? '',
          state: ppData.state ?? '',
          data: ppData.data,
        };
      }

      return new BillingAccountError(
        'Payment processor not found for creditcard payment method',
        404,
      );
    } catch (ppError: any) {
      const statusCode =
        typeof ppError?.response?.status === 'number'
          ? ppError.response.status
          : 500;
      const message =
        ppError?.message ??
        ppError?.response?.statusText ??
        'Failed to fetch payment processor';
      return new BillingAccountError(message, statusCode);
    }
  }

  private async fetchMarketplace(
    client: AuraPortalApi,
    billingID: string,
  ): Promise<Marketplace | BillingAccountError | undefined> {
    try {
      const response = await client.v1AuraPortalAccountsBillingIDMarketplaceGet(
        {
          billingID,
        },
      );
      const marketplace = (response as any)?.marketplace;
      if (marketplace) {
        return {
          id: marketplace.id,
          type: marketplace.type,
          legacy: marketplace.legacy,
          entitlementID: marketplace.entitlementID,
          externalAccountID: marketplace.externalAccountID,
          externalReportingID: marketplace.externalReportingID,
          isActive: marketplace.isActive,
          inactiveReason: marketplace.inactiveReason,
          deletedAt: marketplace.deletedAt,
        };
      }

      return new BillingAccountError(
        'Marketplace not found for marketplace payment method',
        404,
      );
    } catch (mpError: any) {
      const statusCode =
        typeof mpError?.response?.status === 'number'
          ? mpError.response.status
          : 500;
      const message =
        mpError?.message ??
        mpError?.response?.statusText ??
        'Failed to fetch marketplace';
      return new BillingAccountError(message, statusCode);
    }
  }

  // Helper method to fetch overrides for an account
  private async fetchOverrides(
    client: AuraPortalApi,
    billingID: string,
  ): Promise<Override[] | BillingAccountError> {
    try {
      const overridesResponse =
        await client.v1AuraPortalAccountsBillingIDOverridesGet({ billingID });
      const overridesData = (overridesResponse as any)?.data;
      if (Array.isArray(overridesData)) {
        return overridesData.map((o: any) => ({
          ID: o.iD ?? '',
          billingAccountID: o.billingAccountID ?? '',
          createdAt: o.createdAt ?? '',
          createdBy: o.createdBy ?? '',
          comment: o.comment ?? '',
          state: o.state ?? '',
          deletedAt: o.deletedAt,
          deletedBy: o.deletedBy,
          deletionReason: o.deletionReason,
        }));
      }
      return [];
    } catch (overridesError: any) {
      const statusCode =
        typeof overridesError?.response?.status === 'number'
          ? overridesError.response.status
          : 500;
      const message =
        overridesError?.message ??
        overridesError?.response?.statusText ??
        'Failed to fetch overrides';
      return new BillingAccountError(message, statusCode);
    }
  }

  // Helper method to build a complete BillingAccountRecord from a GetResponse
  private async buildBillingAccountRecord(
    client: AuraPortalApi,
    r: GetResponse,
  ): Promise<BillingAccountRecord | BillingAccountError> {
    const account = this.mapToBillingAccount(r);

    // Fetch payment processor if payment method is creditcard
    let paymentProcessor: PaymentProcessor | undefined;
    if (account.paymentMethod === 'creditcard') {
      const ppResult = await this.fetchPaymentProcessor(
        client,
        account.billingID,
      );
      if (ppResult instanceof BillingAccountError) {
        return ppResult;
      }
      paymentProcessor = ppResult;
    }

    // Fetch marketplace if payment method is marketplace
    let marketplace;
    if (account.paymentMethod === 'marketplace') {
      const mpResult = await this.fetchMarketplace(client, account.billingID);
      if (mpResult instanceof BillingAccountError) {
        return mpResult;
      }
      marketplace = mpResult;
    }

    // Fetch overrides
    const overridesResult = await this.fetchOverrides(
      client,
      account.billingID,
    );
    if (overridesResult instanceof BillingAccountError) {
      return overridesResult;
    }

    return {
      billingAccount: account,
      paymentProcessor,
      marketplace,
      overrides: overridesResult,
    };
  }

  // Core method to fetch accounts with different query parameters
  private async fetchAccounts(
    params: V1AuraPortalAccountsGetRequest,
  ): Promise<GetResponse[] | BillingAccountError> {
    const client = await this.getAccountsClient();
    try {
      return await client.v1AuraPortalAccountsGet(params);
    } catch (e: any) {
      const statusCode =
        typeof e?.response?.status === 'number' ? e.response.status : 500;
      const message =
        e?.message ??
        e?.response?.statusText ??
        'Failed to fetch billing account';
      return new BillingAccountError(message, statusCode);
    }
  }

  async getBillingAccountById(
    billingID: string,
  ): Promise<BillingAccountRecord | BillingAccountError> {
    if (!billingID) {
      return new BillingAccountError('billingID is required', 400);
    }

    const listResult = await this.fetchAccounts({ billingID });
    if (listResult instanceof BillingAccountError) {
      return listResult;
    }

    if (!Array.isArray(listResult) || listResult.length === 0) {
      return new BillingAccountError('Billing account not found', 404);
    }
    if (listResult.length > 1) {
      return new BillingAccountError('Multiple billing accounts found', 409);
    }

    const client = await this.getAccountsClient();
    return this.buildBillingAccountRecord(client, listResult[0]);
  }

  async getBillingAccountByProjectId(
    projectID: string,
  ): Promise<BillingAccountRecord | BillingAccountError> {
    if (!projectID) {
      return new BillingAccountError('projectID is required', 400);
    }

    const listResult = await this.fetchAccounts({ projectID });
    if (listResult instanceof BillingAccountError) {
      return listResult;
    }

    if (!Array.isArray(listResult) || listResult.length === 0) {
      return new BillingAccountError('Billing account not found', 404);
    }
    if (listResult.length > 1) {
      return new BillingAccountError('Multiple billing accounts found', 409);
    }

    const client = await this.getAccountsClient();
    return this.buildBillingAccountRecord(client, listResult[0]);
  }

  async getBillingAccountByMarketplaceID(
    marketplaceID: string,
  ): Promise<BillingAccountRecord | BillingAccountError> {
    if (!marketplaceID) {
      return new BillingAccountError('marketplaceID is required', 400);
    }

    try {
      const client = await this.getAccountsClient();
      const mp = await client.v1AuraPortalMarketplaceGet({ id: marketplaceID });

      const billingID = (mp as any)?.billingAccount?.id;
      if (!billingID) {
        return new BillingAccountError(
          'Billing account not found for marketplace ID',
          404,
        );
      }

      return this.getBillingAccountById(String(billingID));
    } catch (e: any) {
      const statusCode =
        typeof e?.response?.status === 'number' ? e.response.status : 500;
      const message =
        e?.message ??
        e?.response?.statusText ??
        'Failed to fetch billing account by marketplace ID';
      return new BillingAccountError(message, statusCode);
    }
  }

  async getBillingAccountByOrgId(
    organizationID: string,
  ): Promise<BillingAccountRecord[] | BillingAccountError> {
    if (!organizationID) {
      return new BillingAccountError('organizationID is required', 400);
    }

    const listResult = await this.fetchAccounts({ organizationID });
    if (listResult instanceof BillingAccountError) {
      return listResult;
    }

    if (!Array.isArray(listResult)) {
      return new BillingAccountError('Billing account not found', 404);
    }

    const client = await this.getAccountsClient();
    const records: BillingAccountRecord[] = [];

    for (const r of listResult) {
      const record = await this.buildBillingAccountRecord(client, r);
      if (record instanceof BillingAccountError) {
        return record;
      }
      records.push(record);
    }

    return records;
  }

  async getBillingAccountByEntitlementID(
    entitlementID: string,
  ): Promise<BillingAccountRecord | BillingAccountError> {
    if (!entitlementID) {
      return new BillingAccountError('entitlementID is required', 400);
    }

    const listResult = await this.fetchAccounts({ entitlementID });
    if (listResult instanceof BillingAccountError) {
      return listResult;
    }

    if (!Array.isArray(listResult) || listResult.length === 0) {
      return new BillingAccountError('Billing account not found', 404);
    }
    if (listResult.length > 1) {
      return new BillingAccountError('Multiple billing accounts found', 409);
    }

    const client = await this.getAccountsClient();
    return this.buildBillingAccountRecord(client, listResult[0]);
  }

  // Create a funding override
  async createOverride(
    billingID: string,
    createdBy: string,
    comment: string,
    state: OverrideState,
  ): Promise<string | BillingAccountError> {
    if (!billingID) {
      return new BillingAccountError('billingID is required', 400);
    }
    if (!comment) {
      return new BillingAccountError('comment is required', 400);
    }
    if (!state) {
      return new BillingAccountError('state is required', 400);
    }
    if (state !== 'funded' && state !== 'unfunded') {
      return new BillingAccountError(
        'state must be either "funded" or "unfunded"',
        400,
      );
    }

    try {
      const client = await this.getAccountsClient();
      const sdkState =
        state === 'funded'
          ? CreateOverrideRequestStateEnum.Funded
          : CreateOverrideRequestStateEnum.Unfunded;
      const response = await client.v1AuraPortalAccountsBillingIDOverridesPost({
        billingID,
        createOverrideRequest: {
          createdBy,
          comment,
          state: sdkState,
        },
      });

      const overrideID = (response as any)?.overrideID;
      if (!overrideID) {
        return new BillingAccountError(
          'Failed to create override: no override ID returned',
          500,
        );
      }

      return overrideID;
    } catch (e: any) {
      return this.extractError(e, 'Failed to create override');
    }
  }

  // Reactivate a suspended marketplace by setting its activation state to active
  async reactivateMarketplace(
    marketplaceID: string,
  ): Promise<void | BillingAccountError> {
    if (!marketplaceID) {
      return new BillingAccountError('marketplaceID is required', 400);
    }

    try {
      const client = await this.getAccountsClient();
      await client.v1AuraPortalAccountsMarketplaceMarketplaceIDActivationStatePut(
        {
          marketplaceID,
          setMarketplaceActivationStateRequest: {
            isActive: true,
            inactiveReason: '',
          },
        },
      );
    } catch (e: any) {
      return this.extractError(e, 'Failed to reactivate marketplace');
    }

    return undefined;
  }

  // Delete a funding override
  async deleteOverride(
    billingID: string,
    overrideID: string,
    deletedBy: string,
    deletionReason: string,
  ): Promise<void | BillingAccountError> {
    if (!billingID) {
      return new BillingAccountError('billingID is required', 400);
    }
    if (!overrideID) {
      return new BillingAccountError('overrideID is required', 400);
    }
    if (!deletionReason) {
      return new BillingAccountError('deletionReason is required', 400);
    }

    try {
      const client = await this.getAccountsClient();
      await client.v1AuraPortalAccountsBillingIDOverridesOverrideIDDelete({
        billingID,
        overrideID,
        deleteOverrideRequest: {
          deletedBy,
          deletionReason,
        },
      });
    } catch (e: any) {
      return this.extractError(e, 'Failed to delete override');
    }

    return undefined;
  }

  async getBillingAccountTransitions(
    billingID: string,
  ): Promise<Transition[] | BillingAccountError> {
    if (!billingID) {
      return new BillingAccountError('billingID is required', 400);
    }

    try {
      const client = await this.getAccountsClient();
      const response = await client.v1AuraPortalAccountsBillingIDTransitionsGet(
        {
          billingID,
        },
      );

      const transitions = (response as any)?.data;
      if (!Array.isArray(transitions)) {
        return [];
      }

      return transitions.map((t: APITransition) => ({
        timestamp: t.timestamp.toISOString(),
        isFunded: t.isFunded,
        unfundedReason: t.unfundedReason,
      }));
    } catch (e: any) {
      return this.extractError(
        e,
        'Failed to fetch billing account transitions',
      );
    }
  }

  // Fetch the product catalogue versions available to move an account onto.
  // The product catalogue publishes these itself, so we call it directly rather
  // than through billing-account-api.
  async getProductCatalogueVersions(): Promise<
    CatalogueVersion[] | BillingAccountError
  > {
    try {
      const client = await this.getProductCatalogueClient();
      const response = await client.v1VersionsGet();

      return (response.data ?? []).map(v => ({
        version: v.version,
        validFrom: v.validFrom.toISOString(),
      }));
    } catch (e: any) {
      return this.extractError(e, 'Failed to fetch product catalogue versions');
    }
  }

  // Move a billing account onto a different product catalogue version
  async setProductCatalogueVersion(
    billingID: string,
    productCatalogueVersion: string,
    reason: string,
  ): Promise<void | BillingAccountError> {
    if (!billingID) {
      return new BillingAccountError('billingID is required', 400);
    }
    if (!productCatalogueVersion) {
      return new BillingAccountError(
        'productCatalogueVersion is required',
        400,
      );
    }
    if (!reason) {
      return new BillingAccountError('reason is required', 400);
    }

    try {
      const client = await this.getAccountsClient();
      // The endpoint answers 204, so there is no account payload to map back;
      // callers refetch the account to pick up the new version.
      await client.v1AuraPortalAccountsBillingIDProductCatalogueVersionPut({
        billingID,
        setProductCatalogueVersionRequest: {
          productCatalogueVersion,
          reason,
        },
      });

      return undefined;
    } catch (e: any) {
      return this.extractError(e, 'Failed to set product catalogue version');
    }
  }

  // Fetch entitlements for a given billing account ID
  async getEntitlementsByBillingId(
    billingID: string,
  ): Promise<Entitlement[] | BillingAccountError> {
    if (!billingID) {
      return new BillingAccountError('billingID is required', 400);
    }

    try {
      const client = await this.getAccountsClient();
      const response = await client.v1AuraPortalEntitlementsGet({
        billingAccountID: billingID,
      });

      const entitlementsData = response.data;
      if (!Array.isArray(entitlementsData)) {
        return [];
      }

      return entitlementsData.map(entitlement =>
        this.mapToEntitlement(entitlement),
      );
    } catch (e: any) {
      return this.extractError(e, 'Failed to fetch entitlements');
    }
  }

  // Fetch entitlement details by entitlement ID
  async getEntitlementById(
    entitlementID: string,
  ): Promise<Entitlement | BillingAccountError> {
    if (!entitlementID) {
      return new BillingAccountError('entitlementID is required', 400);
    }

    try {
      const client = await this.getAccountsClient();
      const response = await client.v1AuraPortalEntitlementsEntitlementIDGet({
        entitlementID,
      });

      // GetEntitlementResponse has the same structure as EntitlementResponse
      // so we can cast it and use the existing mapping method
      return this.mapToEntitlement(response as any);
    } catch (e: any) {
      return this.extractError(e, 'Failed to fetch entitlement details');
    }
  }

  // Create a new entitlement
  async createEntitlement(
    params: CreateEntitlementParams,
  ): Promise<string | BillingAccountError> {
    // Validate required parameters
    if (!params.start) {
      return new BillingAccountError('start date is required', 400);
    }
    if (!params.end) {
      return new BillingAccountError('end date is required', 400);
    }
    if (!params.costBasis) {
      return new BillingAccountError('costBasis is required', 400);
    }
    if (!params.amount || params.amount <= 0) {
      return new BillingAccountError('amount must be greater than 0', 400);
    }
    if (!params.type) {
      return new BillingAccountError('type is required', 400);
    }

    try {
      const client = await this.getAccountsClient();

      // Convert string timestamps to Date objects for the SDK
      const createRequest: CreateEntitlementRequest = {
        start: new Date(params.start),
        end: new Date(params.end),
        costBasis: params.costBasis,
        amount: params.amount,
        type: params.type,
        rolloverPercentage: params.rolloverPercentage, // Include optional rolloverPercentage
        salesforceSubID: params.salesforceSubID, // Include optional salesforceSubID
        provisioningID: params.provisioningID, // Include optional provisioningID
      };

      const response = await client.v1AuraPortalEntitlementsPost({
        createEntitlementRequest: createRequest,
      });

      if (!response.id) {
        return new BillingAccountError(
          'Failed to create entitlement: no ID returned',
          500,
        );
      }

      return response.id;
    } catch (e: any) {
      return this.extractError(e, 'Failed to create entitlement');
    }
  }

  // Update the mutable fields of an unclaimed entitlement
  async updateEntitlement(
    entitlementID: string,
    params: CreateEntitlementParams,
  ): Promise<Entitlement | BillingAccountError> {
    if (!entitlementID) {
      return new BillingAccountError('entitlementID is required', 400);
    }
    if (!params.start) {
      return new BillingAccountError('start date is required', 400);
    }
    if (!params.end) {
      return new BillingAccountError('end date is required', 400);
    }
    if (!params.costBasis) {
      return new BillingAccountError('costBasis is required', 400);
    }
    if (!params.amount || params.amount <= 0) {
      return new BillingAccountError('amount must be greater than 0', 400);
    }
    if (!params.type) {
      return new BillingAccountError('type is required', 400);
    }

    try {
      const client = await this.getAccountsClient();

      const updateRequest: UpdateEntitlementRequest = {
        start: new Date(params.start),
        end: new Date(params.end),
        costBasis: params.costBasis,
        amount: params.amount,
        type: params.type,
        rolloverPercentage: params.rolloverPercentage,
        salesforceSubID: params.salesforceSubID,
        provisioningID: params.provisioningID,
      };

      const response = await client.v1AuraPortalEntitlementsEntitlementIDPut({
        entitlementID,
        updateEntitlementRequest: updateRequest,
      });

      // GetEntitlementResponse has the same structure as EntitlementResponse
      // so we can cast it and use the existing mapping method
      return this.mapToEntitlement(response as any);
    } catch (e: any) {
      return this.extractError(e, 'Failed to update entitlement');
    }
  }

  // Claim an entitlement for a given billing account ID
  async claimEntitlement(
    entitlementID: string,
    billingAccountID: string,
  ): Promise<Entitlement | BillingAccountError> {
    if (!entitlementID) {
      return new BillingAccountError('entitlementID is required', 400);
    }
    if (!billingAccountID) {
      return new BillingAccountError('billingAccountID is required', 400);
    }

    try {
      const client = await this.getAccountsClient();

      const claimRequest: ClaimEntitlementRequest = {
        billingAccountID,
      };

      const response =
        await client.v1AuraPortalEntitlementsEntitlementIDClaimPut({
          entitlementID,
          claimEntitlementRequest: claimRequest,
        });

      // ClaimEntitlementResponse has the same structure as EntitlementResponse
      // so we can cast it and use the existing mapping method
      return this.mapToEntitlement(response as any);
    } catch (e: any) {
      return this.extractError(e, 'Failed to claim entitlement');
    }
  }

  // Void an entitlement by entitlement ID
  async voidEntitlement(
    entitlementID: string,
  ): Promise<Entitlement | BillingAccountError> {
    if (!entitlementID) {
      return new BillingAccountError('entitlementID is required', 400);
    }

    try {
      const client = await this.getAccountsClient();

      // Void the entitlement
      await client.v1AuraPortalEntitlementsEntitlementIDVoidPut({
        entitlementID,
      });

      // Since the void endpoint returns void, we need to fetch the updated entitlement
      // to return the current state using the existing getEntitlementById logic
      return this.getEntitlementById(entitlementID);
    } catch (e: any) {
      return this.extractError(e, 'Failed to void entitlement');
    }
  }

  // Delete an entitlement by entitlement ID
  async deleteEntitlement(
    entitlementID: string,
  ): Promise<void | BillingAccountError> {
    if (!entitlementID) {
      return new BillingAccountError('entitlementID is required', 400);
    }

    try {
      const client = await this.getAccountsClient();
      await client.v1AuraPortalEntitlementsEntitlementIDDelete({
        entitlementID,
      });
      return undefined;
    } catch (e: any) {
      return this.extractError(e, 'Failed to delete entitlement');
    }
  }

  private async extractError(
    err: any,
    baseMessage: string,
  ): Promise<BillingAccountError> {
    const res: Response | undefined = err?.response;
    const statusCode = typeof res?.status === 'number' ? res.status : 500;

    let serverMessage: string | undefined;
    if (res) {
      try {
        const body = await res.clone().json(); // optimistic JSON decode
        serverMessage =
          typeof body?.message === 'string'
            ? body.message
            : JSON.stringify(body);
      } catch {
        try {
          serverMessage = await res.clone().text();
        } catch {
          /* ignore */
        }
      }
    }

    const message = serverMessage
      ? `${baseMessage}: ${serverMessage}`
      : baseMessage;

    return new BillingAccountError(message, statusCode);
  }
}
